import { Injectable, InjectionToken, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { NewsService } from '../news/news.service';
import { Notification, NotificationTarget } from './notification.model';
import { NotificationService } from './notification.service';

/** A target type this library knows a page for, in some application. */
export type OpenableTargetType = 'news' | 'feature-requests' | 'community-inputs' | 'cultures' | 'topics';

/**
 * The target types this application has pages for. A reader's notifications
 * come from every application, so each one says which it can open: the
 * default is news, which every application with notifications mounts.
 *
 * ```ts
 * providers: [{ provide: NOTIFICATION_TARGETS_OPENED_HERE, useValue: ['news', 'feature-requests'] }]
 * ```
 */
export const NOTIFICATION_TARGETS_OPENED_HERE = new InjectionToken<OpenableTargetType[]>('NOTIFICATION_TARGETS_OPENED_HERE', {
  providedIn: 'root',
  factory: () => ['news'],
});

/**
 * Where a notification leads, in this application. The backend names the
 * thing — `{type: 'news', id: '12'}` — and each application decides where it
 * lives, so the bell and the notifications page ask here.
 */
@Injectable({ providedIn: 'root' })
export class NotificationTargetService {
  private newsService = inject(NewsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private openedHere = inject(NOTIFICATION_TARGETS_OPENED_HERE);

  /** Does this application have a page for the target at all? */
  canOpen(target: NotificationTarget | null): target is NotificationTarget {
    return !!target && (this.openedHere as string[]).includes(target.type);
  }

  /**
   * Reads the notification and goes where it leads. Emits whether it went:
   * false when this application has no page for the target, or the thing it
   * names is gone.
   */
  open(notification: Notification): Observable<boolean> {
    if (!notification.read) {
      this.notificationService.markAsRead(notification.id).subscribe({ error: () => undefined });
    }

    if (!this.canOpen(notification.target)) {
      return of(false);
    }

    return this.route(notification.target).pipe(
      tap((route) => {
        if (route) {
          this.router.navigate(route);
        }
      }),
      map((route) => route !== null),
    );
  }

  /**
   * The route to the target, or null when the thing it names is gone — a news
   * item unpublished since, say. Only for a target `canOpen()` accepts.
   */
  route(target: NotificationTarget): Observable<unknown[] | null> {
    switch (target.type) {
      // News is targeted by id, and its page by slug.
      case 'news':
        return this.newsService.findSlug(target.id).pipe(map((slug) => (slug ? ['/news', slug] : null)));
      // An idea is targeted by its slug, which never changes once submitted.
      case 'feature-requests':
        return of(['/ideas', target.id]);
      // A community input is targeted by its slug.
      case 'community-inputs':
        return of(['/contributions/inputs', target.id]);
      // A new insight opens the landing page of the culture or topic subscription that brought it, by slug.
      case 'cultures':
      case 'topics':
        return of(['/' + target.type, target.id]);
      default:
        return of(null);
    }
  }
}
